import React, { useState, useEffect, useCallback } from 'react';
import { Modal, InputNumber, Radio, Space, Typography, message, Spin } from 'antd';
import { WechatOutlined, AlipayOutlined } from '@ant-design/icons';
import { rechargeAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const { Text } = Typography;

interface RechargeConfig {
  wechatEnabled: boolean;
  alipayEnabled: boolean;
  qrCodeTimeout: number;
  autoCloseDelay: number;
}

interface RechargeModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
}

const POINTS_RATIO = 100;

const RechargeModal: React.FC<RechargeModalProps> = ({ visible, onCancel, onSuccess }) => {
  const { updateUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [configLoading, setConfigLoading] = useState(false);
  const [config, setConfig] = useState<RechargeConfig | null>(null);
  const [amount, setAmount] = useState<number>(10);
  const [paymentMethod, setPaymentMethod] = useState<'wechat' | 'alipay'>('wechat');
  const [qrCodeData, setQrCodeData] = useState<{ qrCode: string; orderId: string; amount: string } | null>(null);
  const [qrCodeModalVisible, setQrCodeModalVisible] = useState(false);
  const [countdown, setCountdown] = useState(300);
  const [isQrCodeExpired, setIsQrCodeExpired] = useState(false);

  useEffect(() => {
    if (visible) {
      setConfigLoading(true);
      rechargeAPI.getConfig()
        .then((response) => {
          if (response.data.status === 200) {
            const cfg = response.data.data || response.data;
            setConfig({
              wechatEnabled: cfg.wechatEnabled || cfg.wechatRechargeEnabled || false,
              alipayEnabled: cfg.alipayEnabled || cfg.alipayRechargeEnabled || false,
              qrCodeTimeout: cfg.qrCodeTimeout || 300,
              autoCloseDelay: cfg.autoCloseDelay || 3,
            });
            if (cfg.wechatEnabled || cfg.wechatRechargeEnabled) {
              setPaymentMethod('wechat');
            } else if (cfg.alipayEnabled || cfg.alipayRechargeEnabled) {
              setPaymentMethod('alipay');
            }
          }
        })
        .catch(() => {
          message.error('获取充值配置失败');
        })
        .finally(() => setConfigLoading(false));
    }
  }, [visible]);

  useEffect(() => {
    if (qrCodeModalVisible && countdown > 0 && !isQrCodeExpired) {
      const timer = setTimeout(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            setIsQrCodeExpired(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [qrCodeModalVisible, countdown, isQrCodeExpired]);

  useEffect(() => {
    if (isQrCodeExpired && qrCodeData) {
      const autoCloseDelay = (config?.autoCloseDelay || 3) * 1000;
      const timeoutId = setTimeout(() => {
        const currentOrderId = qrCodeData.orderId;
        setQrCodeModalVisible(false);
        setQrCodeData(null);
        setIsQrCodeExpired(false);
        pollOrderStatus(currentOrderId);
      }, autoCloseDelay);
      return () => clearTimeout(timeoutId);
    }
  }, [isQrCodeExpired, qrCodeData, config]);

  const pollOrderStatus = useCallback(async (orderId: string) => {
    let attempts = 0;
    const maxAttempts = 60;
    const poll = async () => {
      if (attempts >= maxAttempts) return;
      attempts++;
      try {
        const response = await rechargeAPI.getOrderStatus(orderId);
        if (response.data.status === 200) {
          const order = response.data.data || response.data;
          if (order.status === 'paid') {
            message.success('充值成功！');
            setQrCodeModalVisible(false);
            setQrCodeData(null);
            if (updateUser) await updateUser();
            onSuccess();
            return;
          }
          if (order.status === 'failed' || order.status === 'cancelled') {
            message.error('支付失败');
            setQrCodeModalVisible(false);
            setQrCodeData(null);
            return;
          }
        }
        setTimeout(poll, 5000);
      } catch {
        setTimeout(poll, 5000);
      }
    };
    poll();
  }, [onSuccess, updateUser]);

  const handleSubmit = async () => {
    if (!amount || amount <= 0) {
      message.error('请输入有效的充值金额');
      return;
    }

    if (!config?.wechatEnabled && !config?.alipayEnabled) {
      message.error('当前没有可用的支付方式，请联系管理员配置');
      return;
    }

    if (paymentMethod === 'wechat' && !config?.wechatEnabled) {
      message.error('微信支付未启用，请选择其他支付方式');
      return;
    }
    if (paymentMethod === 'alipay' && !config?.alipayEnabled) {
      message.error('支付宝未启用，请选择其他支付方式');
      return;
    }

    setLoading(true);
    try {
      const response = await rechargeAPI.createOrder({
        amount,
        paymentMethod,
      });
      if (response.data.status === 200) {
        const { orderId, qrCode, amount: orderAmount } = response.data.data;
        if (qrCode) {
          setQrCodeData({ qrCode, orderId, amount: String(orderAmount) });
          setQrCodeModalVisible(true);
          setCountdown(config?.qrCodeTimeout || 300);
          setIsQrCodeExpired(false);
          onCancel();
          pollOrderStatus(orderId);
        } else {
          message.warning('支付二维码生成失败，请稍后重试');
        }
      } else {
        message.error(response.data.error || '创建充值订单失败');
      }
    } catch (error: any) {
      message.error(error.response?.data?.error || '创建充值订单失败');
    } finally {
      setLoading(false);
    }
  };

  if (configLoading) {
    return (
      <Modal
        title="账户充值"
        open={visible}
        onCancel={onCancel}
        footer={null}
        centered
      >
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size="large" />
          <div style={{ marginTop: '16px' }}>
            <Text>加载配置中...</Text>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <>
      <Modal
        title="账户充值"
        open={visible}
        onOk={handleSubmit}
        onCancel={onCancel}
        okText="立即支付"
        cancelText="取消"
        confirmLoading={loading}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div>
            <Text strong>充值金额：</Text>
            <InputNumber
              min={0.01}
              max={10000}
              step={10}
              value={amount}
              onChange={(value) => setAmount(value || 10)}
              prefix="¥"
              style={{ width: '100%', marginTop: 8 }}
            />
            <div style={{ marginTop: 8 }}>
              <Text type="secondary">
                将获得 <Text strong style={{ color: '#5080ff' }}>{(amount * POINTS_RATIO).toFixed(0)}</Text> 积分（比例 1元 = {POINTS_RATIO}积分）
              </Text>
            </div>
          </div>
          <div>
            <Text strong>支付方式：</Text>
            {(!config?.wechatEnabled && !config?.alipayEnabled) ? (
              <div style={{ marginTop: 8, color: '#ff4d4f' }}>
                <Text type="danger">当前没有可用的支付方式，请联系管理员配置</Text>
              </div>
            ) : (
              <Radio.Group
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                style={{ marginTop: 8, width: '100%' }}
              >
                <Space size="large" style={{ width: '100%' }}>
                  {config?.wechatEnabled && (
                    <Radio value="wechat">
                      <Space>
                        <WechatOutlined style={{ fontSize: '16px', color: '#07c160' }} />
                        <Text>微信支付</Text>
                      </Space>
                    </Radio>
                  )}
                  {config?.alipayEnabled && (
                    <Radio value="alipay">
                      <Space>
                        <AlipayOutlined style={{ fontSize: '16px', color: '#1677ff' }} />
                        <Text>支付宝</Text>
                      </Space>
                    </Radio>
                  )}
                </Space>
              </Radio.Group>
            )}
          </div>
        </Space>
      </Modal>

      <Modal
        title="扫码支付"
        open={qrCodeModalVisible}
        onCancel={() => {
          setQrCodeModalVisible(false);
          setQrCodeData(null);
          if (qrCodeData) {
            pollOrderStatus(qrCodeData.orderId);
          }
        }}
        footer={null}
        width={400}
        centered
      >
        {qrCodeData && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(qrCodeData.qrCode)}`}
                alt="支付二维码"
                style={{
                  width: '100%',
                  maxWidth: 300,
                  marginBottom: 16,
                  opacity: isQrCodeExpired ? 0.3 : 1,
                  transition: 'opacity 0.3s'
                }}
              />
              {isQrCodeExpired && (
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    maxWidth: 300,
                    height: '100%',
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    color: '#fff',
                    zIndex: 10,
                    marginBottom: 16,
                  }}
                >
                  <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '8px' }}>
                    二维码已过期
                  </div>
                  <div style={{ fontSize: '12px' }}>
                    {config?.autoCloseDelay ? `${config.autoCloseDelay}秒后自动关闭` : '即将关闭'}
                  </div>
                </div>
              )}
            </div>
            <div style={{ marginBottom: 8 }}>
              <Text strong>订单号：{qrCodeData.orderId}</Text>
            </div>
            <div style={{ marginBottom: 8 }}>
              <Text strong>支付金额：¥{qrCodeData.amount}</Text>
            </div>
            <div>
              <Text type={countdown < 60 ? 'danger' : 'secondary'}>
                二维码有效期：{Math.floor(countdown / 60)}:{(countdown % 60).toString().padStart(2, '0')}
              </Text>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default RechargeModal;
